import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackToDashboard from '../components/BackToDashboard';
import { adminListings } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import { AMENITIES, getCategoryLabel } from '../data/amenities';

const TITLE_MAX_LENGTH = 150;
const emptyLocation = { city: '', area: '', country: 'AE' };
const COUNTRY_CODES = ['EG', 'AE', 'SA', 'KW', 'BH', 'OM', 'QA'] as const;
const CURRENCIES = ['AED', 'SAR', 'EGP', 'USD', 'KWD', 'BHD', 'OMR', 'QAR'] as const;
const UNIT_TYPES = ['apartment', 'villa', 'twin_house', 'town', 'studio', 'room'] as const;

export default function ListingForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const formLang = i18n.language === 'ar' ? 'ar' : 'en';
  const { profile } = useAuth();
  const orgId = searchParams.get('orgId') || profile?.orgId || 'org-001';
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = profile?.role === 'admin';
  const [form, setForm] = useState({
    titleEn: '',
    titleAr: '',
    descriptionEn: '',
    descriptionAr: '',
    type: 'rent' as 'rent' | 'sale' | 'short_term',
    category: 'residential' as 'residential' | 'commercial',
    unitType: 'apartment' as 'apartment' | 'villa' | 'twin_house' | 'town' | 'studio' | 'room',
    price: '',
    currency: 'AED',
    location: { ...emptyLocation },
    bedrooms: '',
    bathrooms: '',
    areaSqm: '',
    status: 'draft' as 'draft' | 'pending_review' | 'published' | 'unpublished',
    mediaKeys: [] as string[],
    contactEmail: '',
    contactPhone: '',
    amenities: [] as string[],
  });
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [amenitiesModalOpen, setAmenitiesModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEdit && id) {
      adminListings.get(id, orgId)
        .then((l) => {
          setForm({
            titleEn: l.titleEn || '',
            titleAr: l.titleAr || '',
            descriptionEn: l.descriptionEn || '',
            descriptionAr: l.descriptionAr || '',
            type: l.type || 'rent',
            category: (l.category as 'residential' | 'commercial') || 'residential',
            unitType: (UNIT_TYPES as readonly string[]).includes((l as { unitType?: string }).unitType ?? '') ? ((l as { unitType: typeof form.unitType }).unitType) : 'apartment',
            price: String(l.price ?? ''),
            currency: l.currency || 'AED',
            location: { city: l.location?.city ?? '', area: l.location?.area ?? '', country: l.location?.country ?? 'AE' },
            bedrooms: l.bedrooms != null ? String(l.bedrooms) : '',
            bathrooms: l.bathrooms != null ? String(l.bathrooms) : '',
            areaSqm: l.areaSqm != null ? String(l.areaSqm) : '',
            status: (['draft', 'pending_review', 'published', 'unpublished'].includes(l.status) ? l.status : 'draft') as 'draft' | 'pending_review' | 'published' | 'unpublished',
            mediaKeys: l.mediaKeys || [],
            contactEmail: l.contactEmail ?? '',
            contactPhone: l.contactPhone ?? '',
            amenities: l.amenities ?? [],
          });
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [isEdit, id, orgId]);

  const update = (updates: Partial<typeof form>) => setForm((f) => ({ ...f, ...updates }));

  const isRoom = form.unitType === 'room';

  // Room listings don't have a "bedrooms" count; keep it N/A and omit from payload.
  useEffect(() => {
    if (isRoom && form.bedrooms !== '') update({ bedrooms: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoom]);

  const uploadFiles = async (listingId: string, files: File[]): Promise<string[]> => {
    const contentTypeFallback = 'image/jpeg';
    if (files.length === 0) return [];

    type Presigned = { uploadUrl: string; mediaKey: string; file: File; contentType: string };
    const presignOne = async (file: File): Promise<Presigned> => {
      const contentType = file.type && file.type.startsWith('image/') ? file.type : contentTypeFallback;
      const res = await adminListings.presign(listingId, { fileName: file.name, contentType, orgId });
      return { uploadUrl: res.uploadUrl, mediaKey: res.mediaKey, file, contentType };
    };

    let presigned: Presigned[];
    try {
      presigned = await Promise.all(files.map(presignOne));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('401') || msg.includes('Unauthorized')) throw new Error((t('form.uploadErrorSignIn') || 'Sign in again and try uploading.') + ' ' + (t('form.uploadErrorSignOutHint') || 'Sign out from the menu, then sign in again.'));
      if (msg.includes('403') || msg.includes('Forbidden')) throw new Error(t('form.uploadErrorForbidden') || 'You don’t have permission to upload for this listing.');
      const apiHint = msg.includes('fetch') || msg.includes('reach') || msg.includes('Network')
        ? ' Set VITE_API_BASE_URL in Amplify environment variables and redeploy.'
        : '';
      throw new Error((t('form.uploadErrorPresign') || `Could not get upload link: ${msg}`) + apiHint);
    }

    const putOne = async (p: Presigned): Promise<string> => {
      const putRes = await fetch(p.uploadUrl, { method: 'PUT', body: p.file, headers: { 'Content-Type': p.contentType } });
      if (!putRes.ok) {
        const errText = await putRes.text().catch(() => '');
        const base = t('form.uploadErrorStorage') || 'Upload to storage failed';
        throw new Error(`${base}: ${p.file.name}${errText ? ` — ${errText.slice(0, 60)}` : ''}`);
      }
      return p.mediaKey;
    };

    return Promise.all(presigned.map(putOne));
  };

  const needsContact = form.status === 'pending_review' || form.status === 'published';
  const titleLen = form.titleEn.trim().length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (titleLen > TITLE_MAX_LENGTH) {
      setError(t('form.titleTooLong', { max: TITLE_MAX_LENGTH }));
      return;
    }
    if (!form.contactPhone.trim()) {
      setError(t('form.contactPhoneRequiredAlways'));
      return;
    }
    if (needsContact && !form.contactEmail.trim()) {
      setError(t('form.contactEmailRequired'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        orgId,
        titleEn: form.titleEn.trim().slice(0, TITLE_MAX_LENGTH),
        titleAr: form.titleAr.trim().slice(0, TITLE_MAX_LENGTH) || undefined,
        descriptionEn: form.descriptionEn.trim(),
        descriptionAr: form.descriptionAr.trim() || undefined,
        type: form.type,
        category: form.category,
        unitType: form.unitType,
        price: Number(String(form.price).replace(/,/g, '')) || 0,
        currency: form.currency.trim(),
        location: { city: form.location.city.trim() || undefined, area: form.location.area.trim() || undefined, country: form.location.country.trim() || 'AE' },
        bedrooms: isRoom ? undefined : (form.bedrooms === '' ? undefined : Number(form.bedrooms)),
        bathrooms: form.bathrooms === '' ? undefined : Number(form.bathrooms),
        areaSqm: form.areaSqm === '' ? undefined : Number(form.areaSqm),
        status: form.status,
        mediaKeys: [...form.mediaKeys],
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        amenities: form.amenities.length ? form.amenities : undefined,
      };

      let listingId: string;
      if (isEdit && id) {
        await adminListings.update(id, { ...payload, orgId });
        listingId = id;
      } else {
        const res = await adminListings.create(payload);
        listingId = res.listingId;
      }

      if (newFiles.length > 0) {
        const keys = await uploadFiles(listingId, newFiles);
        await adminListings.update(listingId, { orgId, mediaKeys: [...form.mediaKeys, ...keys] });
      }

      navigate('/dashboard/listings');
    } catch (e) {
      let msg = e instanceof Error ? e.message : t('form.failedToSave');
      if (typeof msg === 'string' && (msg.includes('fetch') || msg.includes('Failed to fetch') || msg.includes('Network') || msg.includes('CORS'))) {
        msg += '. Set VITE_API_BASE_URL in Amplify (Hosting → Environment variables) and redeploy the app.';
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ maxWidth: 640, margin: '0 auto', padding: '2rem' }}>{t('common.loading')}</div>;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <BackToDashboard />
      <Link to="/dashboard/listings" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '1rem', color: '#0d9488', fontWeight: 500, fontSize: '0.9rem', textDecoration: 'none' }}>
        {t('form.backToListings')}
      </Link>
      <h1 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: 700 }}>{isEdit ? t('form.editProperty') : t('form.addProperty')}</h1>

      {!config.apiBaseUrl?.trim() && (
        <div style={{ padding: '1rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, marginBottom: '1rem', fontSize: '0.9rem' }}>
          <strong>API URL not set.</strong> Photo upload and some actions need the backend. In Amplify set <code>VITE_API_BASE_URL</code> to your API URL (e.g. https://xxx.execute-api.us-east-1.amazonaws.com/prod), then redeploy.
        </div>
      )}

      {error && (
        <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: 8, color: '#b91c1c', marginBottom: '1rem' }}>{error}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.titleEn')} * ({t('form.titleMax', { max: TITLE_MAX_LENGTH })})</label>
          <input
            required
            maxLength={TITLE_MAX_LENGTH + 10}
            value={form.titleEn}
            onChange={(e) => update({ titleEn: e.target.value })}
            style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
          />
          <p style={{ fontSize: '0.8rem', color: titleLen > TITLE_MAX_LENGTH ? '#b91c1c' : '#6b7280', marginTop: 4 }}>
            {titleLen} / {TITLE_MAX_LENGTH}
          </p>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.titleAr')}</label>
          <input
            value={form.titleAr}
            onChange={(e) => update({ titleAr: e.target.value })}
            style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.descriptionEn')} *</label>
          <textarea
            required
            rows={4}
            value={form.descriptionEn}
            onChange={(e) => update({ descriptionEn: e.target.value })}
            style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.descriptionAr')}</label>
          <textarea
            rows={4}
            value={form.descriptionAr}
            onChange={(e) => update({ descriptionAr: e.target.value })}
            placeholder={formLang === 'ar' ? 'الوصف بالعربية' : 'Optional Arabic description'}
            dir="rtl"
            style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.type')} *</label>
            <select
              value={form.type}
              onChange={(e) => update({ type: e.target.value as 'rent' | 'sale' | 'short_term' })}
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8 }}
            >
              <option value="rent">{t('form.rent')}</option>
              <option value="sale">{t('form.sale')}</option>
              <option value="short_term">{t('form.shortTerm')}</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.category')}</label>
            <select
              value={form.category}
              onChange={(e) => update({ category: e.target.value as 'residential' | 'commercial' })}
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8 }}
            >
              <option value="residential">{t('form.residential')}</option>
              <option value="commercial">{t('form.commercial')}</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.unitType')} *</label>
          <select
            value={form.unitType}
            onChange={(e) => update({ unitType: e.target.value as typeof form.unitType })}
            style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8 }}
          >
            <option value="apartment">{t('form.unitTypeApartment')}</option>
            <option value="villa">{t('form.unitTypeVilla')}</option>
            <option value="twin_house">{t('form.unitTypeTwinHouse')}</option>
            <option value="town">{t('form.unitTypeTown')}</option>
            <option value="studio">{t('form.unitTypeStudio')}</option>
            <option value="room">{t('form.unitTypeRoom')}</option>
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.price')} *</label>
            <input
              required
              type="text"
              inputMode="numeric"
              value={form.price === '' ? '' : (() => { const n = Number(form.price.replace(/,/g, '')); return Number.isNaN(n) ? form.price : n.toLocaleString('en-US', { maximumFractionDigits: 0 }); })()}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                if (raw === '' || /^\d+$/.test(raw)) update({ price: raw });
              }}
              placeholder="0"
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.currency')}</label>
            <select
              value={form.currency}
              onChange={(e) => update({ currency: e.target.value })}
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8 }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.location')}</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <input
              placeholder={t('form.city')}
              value={form.location.city}
              onChange={(e) => update({ location: { ...form.location, city: e.target.value } })}
              style={{ padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8 }}
            />
            <input
              placeholder={t('form.area')}
              value={form.location.area}
              onChange={(e) => update({ location: { ...form.location, area: e.target.value } })}
              style={{ padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8 }}
            />
            <select
              value={form.location.country}
              onChange={(e) => update({ location: { ...form.location, country: e.target.value } })}
              style={{ padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8 }}
            >
              {COUNTRY_CODES.map((code) => (
                <option key={code} value={code}>{t(`countries.${code}`)}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.contactEmail')} * {needsContact && `(${t('form.requiredForAd')})`}</label>
            <input
              type="email"
              value={form.contactEmail}
              onChange={(e) => update({ contactEmail: e.target.value })}
              placeholder="agent@example.com"
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.contactPhone')} *</label>
            <input
              required
              type="tel"
              value={form.contactPhone}
              onChange={(e) => update({ contactPhone: e.target.value })}
              placeholder="+20 123 456 7890"
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.amenitiesTitle')}</label>
          <button
            type="button"
            onClick={() => setAmenitiesModalOpen(true)}
            style={{
              padding: '0.6rem 1rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              background: '#fff',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            {form.amenities.length ? t('form.amenitiesSelected', { count: form.amenities.length }) : t('form.amenitiesSelect')}
          </button>
          {amenitiesModalOpen && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Amenities and services"
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
              }}
              onClick={() => setAmenitiesModalOpen(false)}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  maxWidth: 520,
                  width: '100%',
                  maxHeight: '85vh',
                  overflow: 'auto',
                  padding: '1.25rem',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600 }}>{t('form.amenitiesTitle')}</h3>
                  <button type="button" onClick={() => setAmenitiesModalOpen(false)} aria-label={t('listingDetail.galleryClose')} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {(['features', 'building', 'health_fitness', 'family_entertainment', 'business_security', 'laundry_kitchen', 'cleaning_maintenance', 'technical'] as const).map((cat) => {
                    const list = AMENITIES.filter((a) => a.category === cat);
                    if (list.length === 0) return null;
                    return (
                      <div key={cat}>
                        <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 8, color: '#374151' }}>{getCategoryLabel(cat, formLang)}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {list.map((a) => {
                            const selected = form.amenities.includes(a.key);
                            return (
                              <button
                                key={a.key}
                                type="button"
                                onClick={() => update({ amenities: selected ? form.amenities.filter((k) => k !== a.key) : [...form.amenities, a.key] })}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: 8,
                                  border: `1px solid ${selected ? '#0d9488' : '#d1d5db'}`,
                                  background: selected ? '#ccfbf1' : '#fff',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  textAlign: formLang === 'ar' ? 'right' : 'left',
                                }}
                              >
                                {formLang === 'ar' ? a.labelAr : a.labelEn}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setAmenitiesModalOpen(false)}
                  style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
                >
                  {t('form.done')}
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.bedrooms')}</label>
            <input
              type="text"
              value={isRoom ? 'N/A' : form.bedrooms}
              onChange={(e) => !isRoom && update({ bedrooms: e.target.value })}
              disabled={isRoom}
              inputMode={isRoom ? undefined : 'numeric'}
              style={{
                width: '100%',
                padding: '0.6rem',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                boxSizing: 'border-box',
                background: isRoom ? '#f9fafb' : '#fff',
                color: isRoom ? '#6b7280' : '#111',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.bathrooms')}</label>
            <input
              type="number"
              min={0}
              value={form.bathrooms}
              onChange={(e) => update({ bathrooms: e.target.value })}
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.areaSqm')}</label>
            <input
              type="number"
              min={0}
              value={form.areaSqm}
              onChange={(e) => update({ areaSqm: e.target.value })}
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.status')}</label>
          <select
            value={form.status}
            onChange={(e) => update({ status: e.target.value as typeof form.status })}
            style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 8 }}
          >
            <option value="draft">{t('form.draft')}</option>
            <option value="pending_review">{t('form.submitForReview')}</option>
            {isAdmin && (
              <>
                <option value="published">{t('form.published')}</option>
                <option value="unpublished">{t('form.unpublished')}</option>
              </>
            )}
          </select>
          {!isAdmin && (
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 4 }}>
              {t('form.draftHelp')}
            </p>
          )}
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('form.photos')}</span>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 8 }}>
            {t('form.photosHelp')}
          </p>
          {form.mediaKeys.length > 0 && (
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: 8 }}>{t('form.imagesAttached', { count: form.mediaKeys.length })}</p>
          )}
          <label
            htmlFor="listing-photos-input"
            style={{
              display: 'block',
              width: '100%',
              padding: '1rem 1.25rem',
              border: '2px dashed #0d9488',
              borderRadius: 8,
              background: '#f0fdfa',
              color: '#0d9488',
              fontWeight: 600,
              fontSize: '1rem',
              cursor: 'pointer',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            {t('form.uploadPhotos')}
            <input
              id="listing-photos-input"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setNewFiles(e.target.files ? Array.from(e.target.files) : [])}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
              }}
              aria-label={t('form.photos')}
            />
          </label>
          {newFiles.length > 0 && <p style={{ fontSize: '0.9rem', color: '#0d9488', marginTop: 4 }}>{t('form.newPhotosWillUpload', { count: newFiles.length })}</p>}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#0d9488',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? t('form.saving') : isEdit ? t('form.updateProperty') : t('form.createProperty')}
          </button>
          <Link to="/dashboard/listings" style={{ padding: '0.75rem 1.5rem', color: '#374151', fontWeight: 500 }}>{t('form.cancel')}</Link>
        </div>
      </form>
    </div>
  );
}
